import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Loader from '../../components/utils/Loader';
import useFetch from '../../hooks/useFetch';
import MainLayout from '../../layouts/MainLayout';
import banner from "./aimasterclass-banner.png";
import AIStories from "./AIStories";

const AIMasterclassLanding = () => {

  const [activeTab, setActiveTab] = useState("Problem");
  const [selectedVideo, setSelectedVideo] = useState(null);


  const tabContent = {
    Problem: (
    <div className='text-black'>
        <h2 className="text-xl font-bold text-blue-700">Problem Statement:</h2>
        <p className='text-black'>
        Despite the abundance of theoretical knowledge available online and
        practical expertise gained through experience, professionals and
        businesses often struggle with unidentified gaps that hinder
        competition, create inefficiencies, and disrupt peace of mind. The
        challenge lies in recognizing and addressing these hidden barriers to
        ensure growth, resilience, and success.
        </p>
    </div>
    ),
    "Best Solution": (
    <div className='text-black'>
        <h2 className="text-xl font-bold text-blue-700">Solution:</h2>
        <p className='text-black'>
        Our <strong>AI Orientation Program</strong> is designed to bridge this
        gap by providing <strong>practical, real-world AI knowledge</strong>
        tailored for professionals, job seekers, business leaders, and
        educators. Unlike theoretical resources, this program offers
        <strong>
            hands-on strategies, industry-specific AI applications, and
            actionable insights
        </strong>
        that empower participants to stay competitive, work smarter, and
        future-proof their careers.
        </p>
        <ul className="list-disc pl-5 justify-center text-black">
        <li>
            <strong>Identify and Overcome Skill Gaps</strong> – Learn how AI is
            transforming industries and how to stay ahead.
        </li>
        <li>
            <strong>Enhance Decision-Making & Efficiency</strong> – Use AI tools
            to automate, analyze, and optimize workflows.
        </li>
        <li>
            <strong>Gain Competitive Edge in Careers & Businesses</strong> –
            Leverage AI for job searches, career transitions, and business
            growth.
        </li>
        <li>
            <strong>Adopt AI in Education & Teaching</strong> – Understand
            AI-driven learning techniques for better student engagement.
        </li>
        <li>
            <strong>Master AI for Innovation & Scaling</strong> – Learn how
            startups and entrepreneurs can harness AI for success.
        </li>
        </ul>
        <p className='text-black'>
        This program <strong>eliminates uncertainty, boosts confidence, and
        provides a structured roadmap</strong> to leverage AI for personal and
        professional growth.
        </p>
    </div>
    ),
    "Interesting Stories": <AIStories />,
    Videos: (
      <div className="flex flex-col items-center text-blue-700">
        {selectedVideo ? (
          <video controls className="w-full max-w-lg">
            <source src={`/videos/${selectedVideo}`} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <ul className="list-none w-full max-w-lg">
            {["video1.mp4", "video2.mp4", "video3.mp4", "video4.mp4"].map((vid) => (
              <li
                key={vid}
                className="cursor-pointer text-blue-600 hover:underline py-2"
                onClick={() => setSelectedVideo(vid)}
              >
                {vid.replace(".mp4", "").replace("video", "AI Video ")}
              </li>
            ))}
          </ul>
        )}
      </div>
    ),
  };


  return (
    <div className="bg-gray-100 mt-12 flex flex-col items-center px-4 sm:px-8">
      {/* Banner Section */}
      <div className="w-full bg-blue-600 text-white text-center py-6 mb-6 rounded-lg relative">
        <img 
            src={banner} 
            alt="AI Masterclass Banner" 
            className="w-full h-96 object-cover rounded-t-lg"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-blue-600 bg-opacity-75 px-4">
            <h1 className="text-2xl sm:text-4xl font-bold">AI For Everyone: A 2-Hour Masterclass</h1>
            <p className="text-lg sm:text-xl mt-2">Learn AI from an industry veteran with 30+ years of experience</p>
        </div>
      </div>

      {/* Tabbed Section */}
      <div className="mb-6 text-black">
        <div className="flex justify-center space-x-4 border-b pb-2 text-black">
          {["Problem", "Best Solution", "Interesting Stories", "Videos"].map((tab) => (
            <button
              key={tab}
              className={`px-4 py-2 rounded-t-lg text-lg text-black font-semibold ${
                activeTab === tab ? "border-b-4 border-blue-600 text-gray-300" : "text-white"
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="p-4 bg-gray-100 rounded-lg mt-4 text-center">{tabContent[activeTab]}</div>
      </div>

      {/* Introduction Section */}
      <div className="text-center max-w-3xl text-black">
        <p className="text-lg sm:text-xl font-semibold text-black">
          Stay ahead in business, careers, and education with expert AI insights.
        </p>
        <p className="mt-2 text-black">🔹 5 Specialized Sessions | 🔹 INR 49 Only | 🔹 Industry-Relevant Insights</p>
      </div>

      {/* Course Schedule */}
      <div className="w-full max-w-4xl mt-6">
        <h2 className="text-xl sm:text-2xl font-bold text-center mb-4 text-blue-700">📅 Course Schedule</h2>
        <div className="overflow-x-auto">
          <table className="w-full bg-gray-700 shadow-md rounded-lg text-sm sm:text-base">
            <thead className="bg-gray-500">
              <tr>
                <th className="py-2 px-4">Date</th>
                <th className="py-2 px-4">Course Title</th>
                <th className="py-2 px-4">Target Audience</th>
                <th className="py-2 px-4">Time (IST)</th>
                <th className="py-2 px-4">Fee (INR)</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Mar 29", "AI for Managers & Business Leaders", "Mid-to-Senior Managers", "7:00 - 9:00 PM", "49"],
                ["Apr 2", "AI for Job Seekers", "Freshers, Working Professionals", "7:00 - 9:00 PM", "49"],
                ["Apr 5", "AI for Career Transitioners", "Professionals at Risk of AI Disruption", "7:00 - 9:00 PM", "49"],
                ["Apr 9", "AI for Educators", "Teachers, Trainers, EdTech Enthusiasts", "7:00 - 9:00 PM", "49"],
                ["Apr 12", "AI for Entrepreneurs & Startups", "Startup Founders, Small Business Owners", "7:00 - 9:00 PM", "49"],
              ].map((course, index) => (
                <tr key={index} className="text-center border-b">
                  {course.map((item, i) => (
                    <td key={i} className="py-2 px-4">{item}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Why Attend Section */}
      <div className="max-w-3xl mt-6 text-center">
        <h2 className="text-xl sm:text-2xl font-bold text-blue-700">Why Attend?</h2>
        <ul className="list-disc mt-2 text-left px-6 sm:px-0">
          <li>✔ Understand AI’s impact on careers, businesses & industries.</li>
          <li>✔ Learn practical AI tools & strategies tailored for your profession.</li>
          <li>✔ Industry-driven insights from an expert with hands-on experience.</li>
          <li>✔ Affordable & time-efficient – Just 2 hours per session!</li>
        </ul>
      </div>

      {/* Call to Action */}
      <div className="mt-6">
        <button className="bg-blue-600 text-white text-lg px-6 py-3 rounded-lg shadow-md hover:bg-blue-700">
          📢 Register Now - Limited Seats!
        </button>
      </div>
    </div>
  );
};

export default AIMasterclassLanding;
